import React from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import YnsIcon from '../../icons/YnsIcon'

const modalSizeMap = {
  sm: '400px',
  md: '800px',
  lg: '1200px',
  xl: '90vw',
}

function Modal({
  actions = null,
  children = [],
  onClose = () => null,
  open = false,
  size = 'sm',
  title = null,
  variant = 'primary',
  onSubmit,
  fullHeight = false,
  icon = null,
  // CTS-9779: optional Close tooltip / aria label (defaults keep other modals working)
  closeButtonLabel = 'Close',
  className = '',
}) {
  let bgColor = 'bg-intoo-blue-medium-800'
  if (variant === 'secondary') {
    bgColor = 'bg-intoo-green-medium-800'
  }

  const paperClassName = ['yns-modal', '@container/yns-modal', className]
    .filter(Boolean)
    .join(' ')

  const renderActions = () => {
    if (!actions) return null

    return (
      <DialogActions
        className="yns-modal-actions w-full !justify-stretch px-4 pb-4 pt-0 sm:px-6"
        sx={{ display: 'block' }}
      >
        {/*
          Keep a flexible row wrapper for callers that pass button arrays.
          Callers that need container-query grids should pass a single
          w-full child (queries resolve against the yns-modal paper).
        */}
        <div className="flex w-full flex-row flex-wrap items-center gap-3">
          {actions}
        </div>
      </DialogActions>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      scroll="paper"
      PaperProps={{
        className: paperClassName,
      }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '20px !important',
          maxWidth: modalSizeMap[size],
        },
      }}
    >
      <DialogTitle
        className={`yns-modal-header text-white ${bgColor}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: '500 !important',
          fontSize: '18px !important',
          lineHeight: '18px !important',
          pr: 1,
          py: 1,
        }}
        component="div"
      >
        {icon && <YnsIcon name={icon} className="h-6 w-6" />} {title}
        <IconButton
          aria-label={closeButtonLabel}
          title={closeButtonLabel}
          onClick={onClose}
          className="text-white hover:bg-white hover:bg-opacity-10 hover:text-white"
          sx={{
            ml: 'auto',
            color: 'white',
            // CTS-9779: visible contrast on dark green header (matches ChatHeader)
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {onSubmit ? (
        <form onSubmit={onSubmit}>
          <DialogContent
            className="yns-modal-body"
            sx={{
              maxHeight: fullHeight
                ? `calc(100vh - ${actions ? '132px' : '60px'})`
                : '60vh',
              overflow: 'auto',
            }}
          >
            <Stack spacing={4} sx={{ py: 3 }}>
              {children}
            </Stack>
          </DialogContent>
          {renderActions()}
        </form>
      ) : (
        <>
          <DialogContent
            className="yns-modal-body"
            sx={{
              maxHeight: fullHeight
                ? `calc(100vh - ${actions ? '132px' : '60px'})`
                : '60vh',
              overflow: 'auto',
            }}
          >
            <Stack spacing={4} sx={{ py: 3 }}>
              {children}
            </Stack>
          </DialogContent>
          {renderActions()}
        </>
      )}
    </Dialog>
  )
}

export default Modal
